/* eslint-disable @typescript-eslint/no-explicit-any */
import { supabase } from './supabase';

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL || process.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || process.env.VITE_SUPABASE_ANON_KEY;

export interface StripeSubscription {
  customer_id: string;
  subscription_id: string | null;
  status: string;
  price_id: string | null;
  current_period_start: number | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
  payment_method_brand: string | null;
  payment_method_last4: string | null;
}

export interface StripeOrder {
  customer_id: string;
  order_id: number;
  checkout_session_id: string;
  payment_intent_id: string;
  amount_subtotal: number;
  amount_total: number;
  currency: string;
  payment_status: string;
  order_status: string;
  order_date: string;
}

export interface Product {
  id: string;
  priceId: string;
  name: string;
  description: string;
  mode: 'payment' | 'subscription';
  price: number;
  currency: string;
  interval?: 'month' | 'year';
}

export const stripeService = {
  async createCheckoutSession(priceId: string, mode: 'payment' | 'subscription') {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
      throw new Error('User not authenticated');
    }

    const baseUrl = window.location.origin;
    const successUrl = `${baseUrl}/success?session_id={CHECKOUT_SESSION_ID}`;
    const cancelUrl = `${baseUrl}/pricing`;

    const response = await fetch(`${SUPABASE_URL}/functions/v1/stripe-checkout`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        price_id: priceId,
        success_url: successUrl,
        cancel_url: cancelUrl,
        mode
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create checkout session');
    }

    const data = await response.json();
    return data;
  },

  async getUserSubscription(userId: string): Promise<StripeSubscription | null> {
    if (!userId) {
      console.error('getUserSubscription called without userId');
      return null;
    }

    // First, get the customer_id for this user
    const { data: customer, error: customerError } = await supabase
      .from('stripe_customers')
      .select('customer_id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (customerError) {
      console.error('Error fetching customer:', customerError);
      throw customerError;
    }

    if (!customer) {
      return null;
    }

    // Get active subscription
    const { data, error } = await supabase
      .from('stripe_subscriptions')
      .select('*')
      .eq('customer_id', customer.customer_id)
      .eq('status', 'active')
      .order('current_period_end', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error fetching active subscription:', error);
      throw error;
    }

    if (data) return data;

    // Fallback to most recent subscription if no active one found
    const { data: fallbackData, error: fallbackError } = await supabase
      .from('stripe_subscriptions')
      .select('*')
      .eq('customer_id', customer.customer_id)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (fallbackError) {
      console.error('Error fetching fallback subscription:', fallbackError);
      return null;
    }

    return fallbackData;
  },

  // In your stripeService.ts
  async getUserSubscription1(userId: string): Promise<StripeSubscription | null> {
    // First get from your database
    const { data: dbSub } = await supabase
      .from('stripe_subscriptions')
      .select('*')
      .eq('user_id', userId)
      .single();

    // If no dates or seems stale, fetch directly from Stripe
    if (dbSub && (!dbSub.current_period_end || dbSub.current_period_end < Date.now() / 1000)) {
      const { data: customer } = await supabase
        .from('stripe_customers')
        .select('customer_id')
        .eq('user_id', userId)
        .single();

      if (customer) {
        const stripeSub = await this.stripe.subscriptions.list({
          customer: customer.customer_id,
          limit: 1
        });

        if (stripeSub.data.length > 0) {
          // Update database
          await supabase
            .from('stripe_subscriptions')
            .update({
              current_period_end: stripeSub.data[0].current_period_end,
              current_period_start: stripeSub.data[0].current_period_start,
              updated_at: new Date().toISOString()
            })
            .eq('subscription_id', dbSub.subscription_id);

          return {
            ...dbSub,
            current_period_end: stripeSub.data[0].current_period_end,
            current_period_start: stripeSub.data[0].current_period_start
          };
        }
      }
    }

    return dbSub;
  },

  // Convenience function to get current user's subscription
  async getCurrentUserSubscription(): Promise<StripeSubscription | null> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user?.id) {
      console.error('No authenticated user found');
      return null;
    }
    return this.getUserSubscription(user.id);
  },

  async getUserOrders(): Promise<StripeOrder[]> {
    const { data, error } = await supabase
      .from('stripe_user_orders')
      .select('*')
      .order('order_date', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Product Pricing
  async fetchStripePrices(): Promise<Product[]> {

    const response = await fetch(`${SUPABASE_URL}/functions/v1/stripe-fetch-prices`, {
      headers: {
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      }
    });


    if (!response.ok) {
      throw new Error('Failed to fetch pricing data');
    }
    return await response.json();
  },

  // Helper to get product by ID
  async getProductById(id: string): Promise<Product | undefined> {
    const products = await this.fetchStripePrices();
    return products.find(product => product.id === id);
  },

  // Helper to get product by price ID
  async getProductByPriceId(priceId: string): Promise<Product | undefined> {
    const products = await this.fetchStripePrices();
    return products.find(product => product.priceId === priceId);
  },

  async createCustomerPortalSession(customerId: string, returnUrl?: string) {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
      throw new Error('User not authenticated');
    }

    const response = await fetch(`${SUPABASE_URL}/functions/v1/stripe-customer-portal`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customer_id: customerId,
        return_url: returnUrl
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create customer portal session');
    }

    return await response.json();
  },


  async getCustomerInvoices(customerId: string) {
    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/fetch-invoices-api?customer_id=${customerId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          mode: 'cors', // <– tell browser to expect CORS
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (err: any) {
      console.error('Network error fetching invoices:', err.message || err);
      throw new Error('Failed to connect to billing service. Please try again later.');
    }
  },



};